import type {
  ShipFitting,
  FittingUpgradeTarget,
} from "@frontier/content/ship-fitting";
import { currentShipFitting } from "@frontier/content/ship-fitting";
import { installedEquipmentFocus, type EquipmentFocus } from "./equipment-focus";
import { fittingSlot, equippedTier } from "./equipment-selection";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import type { PublicEventsView } from "@frontier/content/events";
import { StationActivities } from "./StationActivities";
import type { ContractHistoryControls } from "./use-contract-history";
import type { AchievementControls } from "./use-achievements";
import type { ContractSelection, PassiveId } from "@frontier/content/contracts";
import { equipmentDefinition, type EquipmentKey } from "@frontier/content/arsenal";
import type {
  EquipmentTier,
  EquipmentTiers,
} from "@frontier/content/equipment-tiers";
import type { ArsenalIntent } from "./arsenal-operations";
import { ArsenalPending } from "./ArsenalPending";

import type {
  HullId,
  LoadoutId,
  ModuleId,
  PartyCommand,
  PartyView,
  Profile,
  WeaponId,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { ContractBoard } from "./ContractBoard";
import { EquipmentSummary } from "./EquipmentSummary";
import { EquipmentCatalog } from "./EquipmentCatalog";
import { equipmentSelection } from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";
import { RunDebrief } from "./RunDebrief";

import { tutorialCount, tutorialStage } from "@frontier/content/tutorial";
import { TrainingDockPanel } from "./TrainingDockPanel";
import { HullPicker, nextHullGoal } from "./HullPicker";
import { HullShowcase } from "./HullShowcase";
import { StationMarket } from "./StationMarket";
import { ShipSuppliesDialog } from "./ShipSuppliesDialog";
import { HULLS } from "@frontier/content/definitions";
import "./onboarding.css";
import "./station-dock.css";
import "./station-responsive.css";
import "./station-header.css";
import { StationHeader } from "./StationHeader";
import { credits, moduleLabels } from "./ui-labels";

import type { UpgradePricing } from "./use-upgrade-prices";
interface Props {
  contractHistory?: ContractHistoryControls;
  achievements?: AchievementControls;
  supplies?: ReactNode;
  marketSupplies?: (onOpenHold: () => void) => ReactNode;
  season?: ReactNode;
  onShop?: () => void;
  events: PublicEventsView;
  onJoinEvent: (id: string) => void;
  profile: Profile;
  world: WorldSnapshot;
  party: PartyView;
  targetId: string;
  connected: boolean;
  onParty: (command: PartyCommand) => void;
  busy: boolean;
  onLoadout: (
    loadoutId: LoadoutId,
    weaponId?: WeaponId,
    moduleId?: ModuleId,
    passiveId?: PassiveId,
    secondaryModuleId?: ModuleId,
    hullId?: HullId,
    equipmentTiers?: Partial<EquipmentTiers>,
  ) => Promise<Profile | void>;
  onHull?: (hullId: HullId) => Promise<void>;
  onFitting?: (fitting: ShipFitting) => Promise<Profile | void>;
  onPurchaseHull: (hullId: HullId) => Promise<void>;
  onContracts: (contracts: ContractSelection[]) => Promise<void>;
  onTrainingContract?: () => Promise<void>;
  onCraft: (key: EquipmentKey) => Promise<Profile | void>;
  onDismantle: (key: EquipmentKey, tier: EquipmentTier) => Promise<void>;
  onUpgrade: (
    key: EquipmentKey,
    fromTier: EquipmentTier,
    mount?: FittingUpgradeTarget,
  ) => Promise<Profile | void>;
  upgradePricing: UpgradePricing;
  pendingArsenal: readonly ArsenalIntent[];
  onRetryArsenal: (intent: ArsenalIntent) => Promise<void>;
  onLaunch: () => void;
  onOpenGalaxy: () => void;
  onAccount: () => void;
  onHelp?: () => void;
  onChat: () => void;
  chatUnread: number;
}
export function Hangar({
  contractHistory,
  achievements,
  supplies,
  marketSupplies,
  season,
  onShop,
  events,
  onJoinEvent,
  profile,
  world,
  party,
  targetId,
  connected,
  onParty,
  busy,
  onLoadout,
  onPurchaseHull,
  onFitting,
  onHull,
  onContracts,
  onTrainingContract,
  onCraft,
  onDismantle,
  onUpgrade,
  upgradePricing,
  pendingArsenal,
  onRetryArsenal,
  onLaunch,
  onOpenGalaxy,
  onAccount,
  onHelp,
  onChat,
  chatUnread,
}: Props) {
  const [tab, setTab] = useState<"contracts" | "equipment" | "season" | "shop">(
    "contracts",
  );
  const [marketCategory, setMarketCategory] = useState<"ships" | "equipment">(
    "ships",
  );
  const [suppliesOpen, setSuppliesOpen] = useState(false);
  useEffect(() => setSuppliesOpen(false), [profile.hullId, profile.runId, tab]);
  const [context, setContext] = useState<EquipmentContext | null>(null);
  const [fittingOpen, setFittingOpen] = useState(false);
  const [initialFocus, setInitialFocus] = useState<EquipmentFocus | null>(null);
  const [equipmentAction, setEquipmentAction] = useState<
    "inspect" | "cell" | "install"
  >("inspect");
  useEffect(() => {
    setContext(null);
    setInitialFocus(null);
    setFittingOpen(false);
  }, [profile.playerId, profile.hullId]);
  const [previewHull, setPreviewHull] = useState<HullId>(profile.hullId);
  useEffect(() => setPreviewHull(profile.hullId), [profile.hullId]);
  const result = profile.lastRunResult;
  const trainingMounted = context ? installedEquipmentFocus(profile, context) : null;
  const resumeIntro = profile.tutorial !== null && !profile.tutorial.docked;
  const personalTraining =
    profile.tutorial?.version === 2 && !profile.tutorial.docked;
  const trainingStage =
    profile.tutorial?.version === 2 && !profile.tutorial.docked
      ? tutorialStage(profile.tutorial)
      : null;
  useEffect(() => {
    if (trainingStage === "moduleEquipped") {
      setTab("equipment");
      setContext(null);
      setInitialFocus(null);
      setFittingOpen(true);
    }
    if (trainingStage === "contractAccepted") setTab("contracts");
  }, [trainingStage]);
  const firstReturn =
    profile.tutorial?.docked === true &&
    result?.contracts.some((item) => item.contractId === "intro") === true;
  const eventDeparture = world.publicEvents.some(
    (event) =>
      event.id === events.trackedEventId &&
      (event.status === "available" || event.status === "active"),
  );
  const nextHull = nextHullGoal(profile.ownedHulls);
  const lastRunReport = result && !personalTraining && (
    <details className="dock-report last-run-report" open={firstReturn && profile.tutorial?.version !== 2}>
      <summary>ПОСЛЕДНИЙ ВЫЛЕТ <span>⌄</span></summary>
      <RunDebrief result={result} offers={profile.contractBoard.offers} />
      {firstReturn && profile.tutorial?.version !== 2 && (
        <p className="first-return-hint">
          «{moduleLabels.volley}» открыт — установи во вкладке «Ангар».{" "}
          {nextHull && <span className="hull-goal">Следующий корпус: {nextHull.name} · {credits(profile.credits)} / {credits(nextHull.price)} КР.</span>}
        </p>
      )}
    </details>
  );
  const inspectedHull =
    resumeIntro && !profile.ownedHulls.includes(previewHull)
      ? profile.hullId
      : previewHull;
  const installHull = async (hullId: HullId): Promise<void> => {
    if (onHull) await onHull(hullId);
    else await onLoadout(
          profile.loadoutId,
          profile.weaponId,
          profile.moduleId,
          profile.passiveId,
          profile.secondaryModuleId,
          hullId,
          profile.equipmentTiers,
        );
  };
  const chooseEquipment = async (
    key: EquipmentKey | null,
    tier: EquipmentTier = 0,
  ) => {
    if (!context) return;
    const selected = equipmentSelection(profile, context, key, tier);
    if (!selected) return;
    if (selected.shipFitting) {
      return onFitting?.(currentShipFitting(selected.shipFitting));
    }
    return onLoadout(
      selected.loadoutId,
      selected.weaponId,
      selected.moduleId,
      selected.passiveId,
      selected.secondaryModuleId,
      selected.hullId,
      selected.equipmentTiers,
    );
  };
  return (
    <div className="station-overlay dock-overlay">
      <section
        className={`hangar station-dock ${firstReturn ? "first-return" : ""} ${tab === "equipment" && fittingOpen ? "fitting-mode" : ""}`}
        aria-label="Ангар станции"
        data-training-step={
          personalTraining ? (trainingStage ?? undefined) : undefined
        }
      >
        <StationHeader profile={profile} world={world} party={party} targetId={targetId} connected={connected} onParty={onParty}
          tab={tab} onTab={next => { if (next === "shop") setMarketCategory("ships"); setTab(next); }}
          resumeIntro={resumeIntro} personalTraining={personalTraining}
          departDisabled={busy || trainingStage === "contractAccepted" || trainingStage === "moduleEquipped"}
          freeFlight={profile.selectedContracts.length === 0 && !resumeIntro && !eventDeparture}
          onLaunch={onLaunch} onOpenGalaxy={onOpenGalaxy} onAccount={onAccount} onHelp={onHelp} onShop={onShop} onChat={onChat} chatUnread={chatUnread} />
        {tab === "shop" ? (
          <StationMarket
            profile={profile}
            busy={busy}
            supplies={marketSupplies?.(() => setSuppliesOpen(true))}
            onPurchaseHull={onPurchaseHull}
            onInstallHull={installHull}
            onBuyEquipment={onCraft}
            onHangar={(focus, hullId) => {
              setTab("equipment");
              if (hullId && profile.ownedHulls.includes(hullId)) setPreviewHull(hullId);
              setContext(null);
              setInitialFocus(focus ?? null);
              setFittingOpen(Boolean(focus));
            }}
            pendingArsenal={pendingArsenal}
            onRetryArsenal={onRetryArsenal}
            initialCategory={marketCategory}
          />
        ) : tab === "season" ? (
          <div className="dock-commerce-scroll">{season}</div>
        ) : tab === "contracts" ? (
          <div className="station-contracts">
            <StationActivities
              profile={profile}
              world={world}
              events={events}
              connected={connected}
              onJoinEvent={onJoinEvent}
              {...(contractHistory ? { history: contractHistory } : {})}
              historyContent={lastRunReport}
              latestRunId={result?.runId}
              {...(achievements ? { achievements } : {})}
            >
              {personalTraining && (
                <TrainingDockPanel
                  profile={profile}
                  busy={busy}
                  onAccept={onTrainingContract}
                />
              )}
              {firstReturn && profile.selectedContracts.length === 0 && (
                <p className="first-return-hint">
                  Ты на базе своей фракции. Выбери контракты для следующего
                  вылета — теперь решение за тобой.
                </p>
              )}
              {(firstReturn || !contractHistory) && lastRunReport}
              {resumeIntro ? (
                personalTraining ? null : (
                  <section className="tutorial-resume contract-board">
                    <span className="eyebrow">
                      ПЕРВАЯ ОПЕРАЦИЯ · {tutorialCount(profile.tutorial)}/5
                    </span>
                    <h2>Вернуться к транспорту</h2>
                    <p>
                      {profile.tutorial?.rewardGranted
                        ? "Награда получена. Вернись к транспорту."
                        : "Помоги транспорту и вернись на станцию, чтобы открыть контракты."}
                    </p>
                  </section>
                )
              ) : (
                <ContractBoard
                  offers={profile.contractBoard.offers}
                  selected={profile.selectedContracts}
                  reputation={profile.reputation}
                  knownBlueprints={profile.arsenal.knownBlueprints}
                  busy={busy}
                  onChoose={onContracts}
                />
              )}
            </StationActivities>
          </div>
        ) : (
          <div className="hangar-equipment dock-equipment">
            {personalTraining && (
              <TrainingDockPanel
                profile={profile}
                busy={busy}
                onAccept={onTrainingContract}
                equipmentAction={equipmentAction}
                replacement={trainingMounted ? equipmentDefinition(trainingMounted.key).name : null}
              />
            )}
            <div
              className={`dock-workshop ${fittingOpen ? "fitting-open" : ""}`}
            >
              {pendingArsenal.length > 0 && (
                <div className="dock-pending">
                  <ArsenalPending
                    pending={pendingArsenal}
                    busy={busy}
                    onRetry={onRetryArsenal}
                  />
                </div>
              )}
              {fittingOpen ? (
                <EquipmentCatalog
                  key={`${profile.playerId}:${profile.hullId}`}
                  initialFocus={initialFocus}
                  onTrainingAction={setEquipmentAction}
                  context={context}
                  profile={profile}
                  busy={busy}
                  progressionAllowed={!resumeIntro}
                  onContext={setContext}
                  onClose={() => setFittingOpen(false)}
                  onSelect={chooseEquipment}
                  onMarket={() => {
                    setMarketCategory("equipment");
                    setTab("shop");
                  }}
                  onDismantle={onDismantle}
                  onUpgrade={(key, tier) =>
                    onUpgrade(
                      key,
                      tier,
                      profile.shipFitting &&
                        context &&
                        equippedTier(profile, context, key) === tier
                        ? { hullId: profile.hullId, slot: fittingSlot(context) }
                        : undefined,
                    )
                  }
                  upgradePricing={upgradePricing}
                />
              ) : (
                <>
                  <HullShowcase
                    {...(supplies && inspectedHull === profile.hullId
                      ? { onSupplies: () => setSuppliesOpen(true) }
                      : {})}
                    hullId={inspectedHull}
                    installed={profile.hullId}
                    owned={profile.ownedHulls}
                    balance={profile.credits}
                    components={profile.components}
                    purchasingAllowed={!resumeIntro}
                    busy={busy}
                    onInstall={installHull}
                    onPurchase={onPurchaseHull}
                  />
                  <aside
                    className="dock-module-panel"
                    aria-label="Оборудование корабля"
                  >
                    <h2 className="dock-equipment-title">ОСНАЩЕНИЕ</h2>
                    <button
                      className="dock-inventory-entry"
                      data-equipment-action="open-inventory"
                      disabled={busy}
                      onClick={() => {
                        setContext(null);
                        setInitialFocus(null);
                        setFittingOpen(true);
                      }}
                    >
                      ОТКРЫТЬ ИНВЕНТАРЬ →
                    </button>
                    <EquipmentSummary
                      profile={profile}
                      onOpen={(slot) => {
                        setContext(slot);
                        setInitialFocus(null);
                        setFittingOpen(true);
                      }}
                      busy={busy}
                    />
                  </aside>
                </>
              )}
            </div>
            <HullPicker
              ownedOnly
              selected={profile.hullId}
              previewed={inspectedHull}
              owned={profile.ownedHulls}
              purchasingAllowed={!resumeIntro}
              busy={busy}
              onPreview={setPreviewHull}
            />
          </div>
        )}
        {suppliesOpen && (
          <ShipSuppliesDialog
            shipName={HULLS[profile.hullId].name}
            returnTo={tab === "shop" ? "магазин" : "ангар"}
            onClose={() => setSuppliesOpen(false)}
          >
            {supplies}
          </ShipSuppliesDialog>
        )}
      </section>
    </div>
  );
}
